import React, { useCallback, useMemo, useState } from 'react'
import { flushSync } from 'react-dom'
import { FormProvider, useForm } from 'react-hook-form'
import { VStack } from '@musinsa/mwms-ds'
import { omitBy } from 'lodash-es'
import { useParsedQuery } from '@/hooks/useParsedQuery'
import { PageLayout } from '@/components/layout/PageLayout'
import TitleSection from '@/components/content/TitleSection'
import { GetOutboundsReq } from '@/services/outbound/outboundSearch/types'
import { useExportExcelOnOutbounds, useGetOutbounds } from '@/services/outbound/outboundSearch/useOutboundSearchQuries'
import OutboundSearchOrderForm, { getInitForm } from '@/pages/outbound/outboundSearchOrder/components/OutboundSearchOrderForm'
import { OutboundSearchOrderTable } from './components/OutboundSearchOrderTable'
import { GRID_SIZE } from '@/utils/grid/grid'
import { MenuStorage } from '@/utils/storage/MenuStorage'
import { useMenuOnSession } from '@/components/layout/MainMenuOnSessionProvider'
import { useShipperFieldSelection } from '@/components/system/ShipperSelector'
import { SHIPPER_TYPES } from '@/services/shipper/apis'
import { MENU_CODE } from './constants'

function OutboundSearchOrder() {
  const { reload } = useMenuOnSession()
  const { parsedQs, replaceQuery } = useParsedQuery<GetOutboundsReq & { isShipperNamesSelectedAll?: boolean }>()

  const form = useForm<GetOutboundsReq>({ defaultValues: getInitForm(parsedQs) })

  const [queryKey, setQueryKey] = useState(form.getValues())
  const [currentPage, setCurrentPage] = useState(0)

  const { isAllSelected, handleShipperFieldChange, resetShipperSelection, processShipperDataForSubmit } = useShipperFieldSelection<GetOutboundsReq>(
    {
      logisticsServiceTypes: parsedQs?.logisticsServiceTypes?.length === SHIPPER_TYPES.length,
      shipperNames: String(parsedQs?.isShipperNamesSelectedAll) === 'true',
    },
  )

  const { onExportData } = useExportExcelOnOutbounds({ ...parsedQs, page: currentPage, size: GRID_SIZE.MEDIUM }, MENU_CODE)
  const { data, refetch, isFetching } = useGetOutbounds({ ...queryKey, page: currentPage, size: GRID_SIZE.MEDIUM }, MENU_CODE)

  const handleReset = useCallback(() => {
    form.reset(getInitForm())
    resetShipperSelection()
  }, [form, resetShipperSelection])

  const handleSubmit = useCallback(
    async (formData: GetOutboundsReq) => {
      const processedData = processShipperDataForSubmit(formData)
      const query = omitBy(processedData, (value) => value === '') as GetOutboundsReq
      MenuStorage.menuOnSession.setSearch({ ...formData, isShipperNamesSelectedAll: isAllSelected.shipperNames })

      // TODO: [SCOPE B] 아래 다건 입력 구조 데이터 타입을 배열 -> 스트링으로 변환 필요 (API 수정 후 아래 코드 삭제 필요)
      if (typeof query?.orderNumbers === 'string') {
        query.orderNumbers = [query?.orderNumbers]
      }
      if (typeof query?.orderOptionNumbers === 'string') {
        query.orderOptionNumbers = [query?.orderOptionNumbers]
      }
      if (typeof query?.orderConsolNumbers === 'string') {
        query.orderConsolNumbers = [query?.orderConsolNumbers]
      }
      if (typeof query?.uids === 'string') {
        query.uids = [query?.uids]
      }
      if (typeof query?.barcodes === 'string') {
        query.barcodes = [query?.barcodes]
      }
      if (typeof query?.invoiceNumbers === 'string') {
        query.invoiceNumbers = [query?.invoiceNumbers]
      }
      if (typeof query?.salChannels === 'string') {
        query.salChannels = [query?.salChannels]
      }

      flushSync(() => {
        setQueryKey(query)
        setCurrentPage(0)
      })

      reload()
      await refetch()
      replaceQuery({ ...query, isShipperNamesSelectedAll: isAllSelected.shipperNames })
    },
    [processShipperDataForSubmit, reload, refetch, replaceQuery],
  )

  const onChangePage = useCallback(async (page: number) => {
    flushSync(() => setCurrentPage(page))
    await refetch()
  }, [])

  return (
    <FormProvider {...form}>
      <PageLayout gap={16}>
        <VStack gap={12}>
          <TitleSection variant="legacy" title="출고조회 (주문별)" />
          <OutboundSearchOrderForm onSubmit={handleSubmit} isLoading={isFetching} onFieldChange={handleShipperFieldChange} onReset={handleReset} />
        </VStack>
        <OutboundSearchOrderTable
          items={data?.items || []}
          pageInfo={data?.pageInfo}
          isLoading={isFetching}
          currentPage={currentPage}
          onChangePage={onChangePage}
          onExportData={onExportData}
        />
      </PageLayout>
    </FormProvider>
  )
}

export default OutboundSearchOrder
